/* eslint-disable react/prop-types */
import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { createRef, useEffect, useState } from "react"
import Loader from "../../../components/Loader"
import { EditorTools } from "@progress/kendo-react-editor"
import {
    clearEndorsement,
    getEndorsement,
    actions,
    addEndorsementFile,
    editEndorsement,
    getEndorsements,
    addEndorsement,
    deleteEndorsementFile
} from "../../../data/endorsementSlice"
import RichTextEditor from "../../../components/RichTextEditor/RichTextEditor"
import { getEndorsementAppliesToList, getEndorsementTypes, getLogicalOperatorTypes } from "../../../data/enumSlice"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { css } from "@emotion/react"
import RemoveOrderedListTool from "../../../components/RemoveOrderedListTool"
import OrderedListTool from "../../../components/OrderedListTool"
import { byPrefixAndName } from "@awesome.me/kit-de80aecb6a/icons"

const componentStyle = css`
    p {
        font-size: 14px;
    }
`

const { updateEndorsement } = actions

const {
    Bold,
    Italic,
    Underline,
    Strikethrough,
    Subscript,
    Superscript,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Indent,
    Outdent,
    OrderedList,
    UnorderedList,
    Undo,
    Redo,
    FontSize,
    FontName,
    FormatBlock,
    Link,
    Unlink,
    InsertImage,
    ViewHtml,
    InsertTable,
    AddRowBefore,
    AddRowAfter,
    AddColumnBefore,
    AddColumnAfter,
    DeleteRow,
    DeleteColumn,
    DeleteTable,
    MergeCells,
    SplitCell
} = EditorTools

const AddEndorsementsModal = ({ show, onHide, selectedRowId }) => {
    const { endorsementTypes, logicalOperatorTypes, endorsementAppliesToList } = useSelector((state) => state.enums)
    const { endorsement, fetchStatus } = useSelector((state) => state.endorsement)
    const dispatch = useDispatch()

    const fileInputRef = createRef()

    const [selectedFile, setSelectedFile] = useState({})
    const hasSelectedFile = Object.keys(selectedFile)?.length > 0

    useEffect(() => {
        if (endorsement?.formLayout) {
            let editorInfo = document.getElementsByClassName("ProseMirror")[0]
            if (editorInfo) {
                editorInfo.setAttribute("tabindex", 11)
            } else {
                reviseModal()
            }
        }

        if (endorsement?.files?.length) {
            setSelectedFile(endorsement.files[0])
        } else setSelectedFile({})
    }, [endorsement])

    useEffect(() => {
        if (show) {
            reviseModal()
        }
    }, [show])

    const reviseModal = () => {
        setTimeout(() => {
            removeTag()
        }, 10)
    }

    const removeTag = () => {
        let editorInfo = document.getElementsByClassName("modal")[0]

        if (editorInfo) {
            editorInfo.removeAttribute("tabindex")
        } else {
            reviseModal()
        }
    }

    useEffect(() => {
        selectedRowId && show && dispatch(getEndorsement(selectedRowId))
        return () => dispatch(clearEndorsement())
    }, [selectedRowId, show])

    useEffect(() => {
        !endorsementTypes.length && dispatch(getEndorsementTypes())
        !logicalOperatorTypes.length && dispatch(getLogicalOperatorTypes())
        !endorsementAppliesToList.length && dispatch(getEndorsementAppliesToList())
    }, [])

    const update = (e, field) => {
        if (field) {
            dispatch(updateEndorsement({ field, value: e }))
            return true
        }
        const { name, type, value, checked } = e.currentTarget

        if (type === "checkbox") {
            dispatch(updateEndorsement({ field: name, value: checked }))
            return true
        }

        let updatedValue

        if (type === "number") {
            updatedValue = parseFloat(value)
        } else {
            updatedValue = value
        }

        dispatch(updateEndorsement({ field: name, value: updatedValue }))

        if (name === "formLayout") {
            setSelectedFile(null)
        }
    }

    const uploadFile = async (file) => {
        const { payload } = await dispatch(addEndorsementFile(file))
        dispatch(
            updateEndorsement({
                field: "files",
                value: [{ id: payload?.id, name: payload?.name, mimeType: payload?.mimeType }]
            })
        )
    }

    const onSubmit = async () => {
        if (selectedRowId) {
            dispatch(
                editEndorsement({
                    ...endorsement,
                    id: selectedRowId
                })
            ).then(() => {
                dispatch(getEndorsements()).then(() => {
                    dispatch(clearEndorsement())
                    setSelectedFile({})
                })
            })
            onHide()
        } else {
            dispatch(addEndorsement(endorsement)).then(() => {
                dispatch(getEndorsements()).then(() => {
                    dispatch(clearEndorsement())
                    setSelectedFile({})
                })
            })
            onHide()
        }
    }

    const handleFileInputChange = (e) => {
        setSelectedFile(e?.target?.files[0])
        uploadFile(e?.target?.files[0])
        dispatch(updateEndorsement({ field: "formLayout", value: "" }))
    }

    const handleResetClick = () => {
        dispatch(deleteEndorsementFile(selectedFile?.id)).then(({ payload }) => {
            if (payload || payload?.succeeded) {
                setSelectedFile({})
                dispatch(
                    updateEndorsement({
                        field: "files",
                        value: [{}]
                    })
                )
                if (fileInputRef.current) {
                    fileInputRef.current.value = ""
                }
            }
        })
    }

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            aria-labelledby="contained-modal-title-vcenter"
            centered
            css={componentStyle}>
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter">
                    {selectedRowId ? "Edit" : "Add"} Endorsement
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Loader show={fetchStatus === "loading"} />

                <Row>
                    <Col sm={6}>
                        <Form.Group
                            className="mb-3"
                            controlId="name">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                className="form-control-sm"
                                type="text"
                                name="name"
                                value={endorsement?.name || ""}
                                onChange={update}
                                tabIndex={1}
                            />
                        </Form.Group>
                        <Form.Group
                            className="mb-3"
                            controlId="appliesTo">
                            <Form.Label>Applies To</Form.Label>
                            <Form.Select
                                className="form-control-sm"
                                size="sm"
                                aria-label="select"
                                value={endorsement?.appliesTo || ""}
                                onChange={update}
                                name="appliesTo"
                                tabIndex={3}>
                                <option value={""}>--- Select ---</option>
                                {endorsementAppliesToList?.map(({ name, value }) => (
                                    <option
                                        key={value}
                                        value={value}>
                                        {name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group
                            className="mb-3"
                            controlId="type">
                            <Form.Label>Type</Form.Label>
                            <Form.Select
                                size="sm"
                                className="form-control-sm"
                                aria-label="select"
                                value={endorsement?.type || ""}
                                onChange={update}
                                tabIndex={5}
                                name="type">
                                <option value={""}>--- Select ---</option>

                                {endorsementTypes?.map(({ name, value }) => (
                                    <option
                                        key={value}
                                        value={value}>
                                        {name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group
                            className="mb-3"
                            controlId="logicalOperator">
                            <Form.Label>Logical Operator</Form.Label>
                            <Form.Select
                                className="form-control-sm"
                                size="sm"
                                aria-label="select"
                                value={endorsement?.logicalOperator || ""}
                                onChange={update}
                                tabIndex={7}
                                name="logicalOperator">
                                <option value={""}>--- Select ---</option>
                                {logicalOperatorTypes?.map(({ name, value }) => (
                                    <option
                                        key={value}
                                        value={value}>
                                        {name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group
                            className="mb-3"
                            controlId="statesCsv">
                            <Form.Label>States CSV</Form.Label>
                            <Form.Control
                                className="form-control-sm"
                                as="textarea"
                                name="statesCsv"
                                value={endorsement?.statesCsv || ""}
                                onChange={update}
                                rows={1}
                                tabIndex={9}
                            />
                        </Form.Group>
                        {(!endorsement?.formLayout || endorsement?.formLayout === "<p></p>") && (
                            <Form.Group
                                className="mb-3"
                                controlId="claimsMade">
                                <Form.Label>Upload Document</Form.Label>
                                <Form.Control
                                    className="form-control-sm"
                                    type="file"
                                    accept=".pdf, .doc, .docx"
                                    name="applicationFile"
                                    onChange={handleFileInputChange}
                                    ref={fileInputRef}
                                    disabled={hasSelectedFile}
                                    tabIndex={11}
                                />
                                {hasSelectedFile && (
                                    <div className="pt-2">
                                        <strong> {selectedFile.name}</strong>
                                        <span
                                            onClick={handleResetClick}
                                            className="ps-2 cursor-pointer"
                                            disabled={!selectedFile?.id}>
                                            <FontAwesomeIcon
                                                icon={byPrefixAndName.fal["trash"]}
                                                className="icon-red"
                                            />
                                        </span>
                                    </div>
                                )}
                            </Form.Group>
                        )}
                    </Col>
                    <Col sm={6}>
                        <Form.Group
                            className="mb-3"
                            controlId="number">
                            <Form.Label>Number</Form.Label>
                            <Form.Control
                                className="form-control-sm"
                                type="text"
                                name="number"
                                value={endorsement?.number || ""}
                                onChange={update}
                                tabIndex={2}
                            />
                        </Form.Group>
                        <Form.Group
                            className="mb-3"
                            controlId="edition">
                            <Form.Label>Edition</Form.Label>
                            <Form.Control
                                className="form-control-sm"
                                type="text"
                                name="edition"
                                value={endorsement?.edition || ""}
                                onChange={update}
                                tabIndex={4}
                            />
                        </Form.Group>
                        <Form.Group
                            className="mb-3"
                            controlId="tagRule">
                            <Form.Label>Tag Rule</Form.Label>
                            <Form.Control
                                className="form-control-sm"
                                as="textarea"
                                name="tagRule"
                                value={endorsement?.tagRule || ""}
                                onChange={update}
                                rows={1}
                                tabIndex={6}
                            />
                        </Form.Group>
                        <Form.Group
                            className="mb-3"
                            controlId="footerStatesCsv">
                            <Form.Label>Footer CSV</Form.Label>
                            <Form.Control
                                className="form-control-sm"
                                as="textarea"
                                name="footerStatesCsv"
                                value={endorsement?.footerStatesCsv || ""}
                                onChange={update}
                                rows={1}
                                tabIndex={8}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                {!hasSelectedFile && (
                    <Row>
                        <Col>
                            <Form.Label>Form Layout</Form.Label>
                            <RichTextEditor
                                tools={[
                                    [Bold, Italic, Underline, Strikethrough],
                                    [Subscript, Superscript],
                                    [AlignLeft, AlignCenter, AlignRight, AlignJustify],
                                    [Indent, Outdent],
                                    [OrderedList, UnorderedList],
                                    FontSize,
                                    FontName,
                                    FormatBlock,
                                    [Undo, Redo],
                                    [Link, Unlink, InsertImage, ViewHtml],
                                    [InsertTable],
                                    [AddRowBefore, AddRowAfter, AddColumnBefore, AddColumnAfter],
                                    [DeleteRow, DeleteColumn, DeleteTable],
                                    [MergeCells, SplitCell],
                                    RemoveOrderedListTool,
                                    OrderedListTool
                                ]}
                                content={endorsement?.formLayout}
                                handleOnChange={(event) => {
                                    update(event.html, "formLayout")
                                }}
                            />
                        </Col>
                    </Row>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="secondary"
                    onClick={onHide}
                    className="secondary-button btn-sm">
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    type="button"
                    className="primary-button btn-sm"
                    onClick={onSubmit}>
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default AddEndorsementsModal
